import { useState } from 'react';
import { ExternalLink, Play } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import SvgIcon from '@/components/Icon/SvgIcon';
import type { IconName } from '@/utils/iconName';
import { YoutubePlayer } from '../../components/YoutubePlayer';
import i18n from '@/utils/i18n';

// Same contact page the SustainabilityReports tab points at for its
// "you don't have this yet" case, so both add-on asks land in one place.
const CONTACT_URL = 'https://www.vytal.org/de/kontakt';

type Resource = {
  /** Suffix for the `Manuals:<key>Title` / `Manuals:<key>Description` keys. */
  key: string;
  iconName: IconName;
  /** Figma slide deck. Mutually exclusive with `videoId`. */
  href?: string;
  /** YouTube id — renders a click-to-load player instead of a link. */
  videoId?: string;
  /**
   * Optional preview image. Drop a PNG into `src/assets/manuals/`, import it,
   * and set it here — the card swaps the icon tile for the image on its own.
   */
  thumbnail?: string;
};

// The decks are one multi-language file each, so there is no per-locale link.
const INCLUDED: Resource[] = [
  {
    key: 'papp',
    iconName: 'app-phone',
    href: 'https://www.figma.com/slides/Ot9PDKEM6Y7WNaYrD29AC5',
  },
  {
    key: 'everphone',
    iconName: 'phone',
    href: 'https://www.figma.com/slides/vkWmI9rrP1scBqTT5hKdPk',
  },
  {
    key: 'inventory',
    iconName: 'clipboard-container-check',
    href: 'https://www.figma.com/slides/AYHdNKT9i7aq2ID4XcAEPv',
  },
  {
    key: 'marketing',
    iconName: 'bullhorn-marketing',
    videoId: 'xkhogE7tyqw',
  },
];

// Features a partner may not have yet. We deliberately do not claim to know
// which ones this partner has: `feature_tap_to_borrow_enabled` hangs off the
// User, not the Store, and Superset access is a separate AnalyticsUser record —
// so a per-store answer would be a guess. The section says "not everyone has
// these, ask us" instead, which is true either way.
const ADD_ONS: Resource[] = [
  {
    key: 'tap2borrowBasic',
    iconName: 'contactless-card',
    href: 'https://www.figma.com/slides/jMA5v13mD3qtINxYxF0tNA',
  },
  {
    key: 'tap2borrowProfessional',
    iconName: 'contactless-card',
    href: 'https://www.figma.com/slides/bqPdurPr16AsEb9ARROBru',
  },
  {
    key: 'tap2borrowProfessionalSetup',
    iconName: 'settings-gear',
    href: 'https://www.figma.com/slides/nnuT21OZlvpOCqiwufOcaL',
  },
  {
    key: 'superset',
    iconName: 'bar-chart-arrow',
    href: 'https://www.figma.com/deck/TMoVlHWwzIrGKbovKNLYAu',
  },
];

const ResourceCard = ({
  resource,
  isAddOn = false,
}: {
  resource: Resource;
  isAddOn?: boolean;
}) => {
  const [playing, setPlaying] = useState(false);

  const title = i18n.t(`Manuals:${resource.key}Title`);

  return (
    <Card className="tw:w-full tw:gap-0 tw:pt-0">
      {playing && resource.videoId ? (
        // Flex, so the iframe is blockified: the Partner Portal renders inside
        // `[data-use-bs]`, which resets `display` on iframe/img/svg (main.css).
        <div className="tw:bg-muted tw:aspect-video tw:flex tw:w-full">
          <YoutubePlayer videoId={resource.videoId} />
        </div>
      ) : (
        <button
          type="button"
          // A video opens in place; a deck opens in a new tab. Either way the
          // whole tile is the target, so the card is clickable, not just the
          // footer button.
          onClick={() => {
            if (resource.videoId) {
              setPlaying(true);
            } else if (resource.href) {
              window.open(resource.href, '_blank', 'noopener,noreferrer');
            }
          }}
          aria-label={title}
          className="tw:bg-muted tw:aspect-video tw:relative tw:flex tw:w-full tw:cursor-pointer tw:items-center tw:justify-center tw:overflow-hidden tw:transition-colors tw:hover:bg-secondary"
        >
          {resource.thumbnail ? (
            <img
              src={resource.thumbnail}
              alt=""
              className="tw:h-full tw:w-full tw:object-cover"
            />
          ) : (
            <SvgIcon
              name={resource.iconName}
              size={56}
              className="tw:text-foreground/55"
            />
          )}
          {resource.videoId && (
            <span className="tw:bg-background/90 tw:absolute tw:flex tw:size-11 tw:items-center tw:justify-center tw:rounded-full tw:shadow-xs">
              <Play className="tw:size-4" />
            </span>
          )}
        </button>
      )}

      <CardHeader className="tw:pt-4">
        <CardTitle>{title}</CardTitle>
        {isAddOn && (
          <Badge variant="secondary" className="tw:mt-1.5 tw:w-fit">
            {i18n.t('Manuals:addOnBadge')}
          </Badge>
        )}
        <CardDescription className="tw:mt-1">
          {i18n.t(`Manuals:${resource.key}Description`)}
        </CardDescription>
      </CardHeader>

      <CardFooter className="tw:mt-auto tw:pt-4">
        {resource.videoId ? (
          <Button
            variant="outline"
            size="sm"
            onClick={() => setPlaying(true)}
            disabled={playing}
          >
            <Play data-icon="inline-start" />
            {i18n.t('Manuals:watchVideo')}
          </Button>
        ) : (
          <Button variant="outline" size="sm" asChild>
            {/* Bootstrap scope styles bare anchors, and `asChild` makes this
                anchor the button itself. Without these, the Partner Portal
                underlines the label and — in dark mode — paints it near-black
                on a near-black card, i.e. invisible. */}
            <a
              href={resource.href}
              target="_blank"
              rel="noopener noreferrer"
              className="tw:text-inherit! tw:no-underline!"
            >
              <ExternalLink data-icon="inline-start" />
              {i18n.t('Manuals:openManual')}
            </a>
          </Button>
        )}
      </CardFooter>
    </Card>
  );
};

type MerchantManualsTabProps = {
  title: string;
};

/**
 * Resource library for partners: the Figma manuals plus the marketing video.
 * Rendered identically in the admin frontend (MerchantStorePageContent) and the
 * Partner Portal (PartnerMerchantStorePage) so an account manager on the phone
 * sees exactly what the partner sees.
 *
 * The content is the same for every store, so this component reads no store
 * data and issues no queries. It replaced an onboarding gate that unlocked a
 * "go live" button once three YouTube videos had played to the end; that gate
 * only ever rendered on the employee-only route, and the same `coming_soon`
 * flag is toggled by a plain checkbox in MerchantGeneralAdminInfoTab.
 */
export const MerchantManualsTab = ({ title }: MerchantManualsTabProps) => {
  return (
    <div className="tw:flex tw:flex-col tw:gap-8 tw:p-4">
      <div className="tw:flex tw:flex-col tw:gap-2">
        {/*
          `!` so the heading is identical in both hosts: the Partner Portal
          renders inside `[data-use-bs]`, where Bootstrap's scoped `h4` rule
          would otherwise shrink this to 22.5px while the admin frontend
          shows 36px.
        */}
        <h4 className="tw:text-4xl! tw:font-semibold!">{title}</h4>
        <span className="tw:text-muted-foreground tw:max-w-3xl">
          {i18n.t('Manuals:intro')}
        </span>
      </div>

      <section className="tw:flex tw:flex-col tw:gap-4">
        <h5 className="tw:text-lg! tw:font-medium!">
          {i18n.t('Manuals:includedHeading')}
        </h5>
        <div className="tw:grid tw:grid-cols-[repeat(auto-fill,minmax(260px,1fr))] tw:gap-4">
          {INCLUDED.map((resource) => (
            <ResourceCard key={resource.key} resource={resource} />
          ))}
        </div>
      </section>

      <section className="tw:flex tw:flex-col tw:gap-4">
        <div className="tw:flex tw:flex-col tw:gap-2">
          <h5 className="tw:text-lg! tw:font-medium!">
            {i18n.t('Manuals:addOnsHeading')}
          </h5>
          <span className="tw:text-muted-foreground tw:max-w-3xl">
            {i18n.t('Manuals:addOnsInfo')}{' '}
            <a
              href={CONTACT_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="tw:text-primary tw:underline tw:underline-offset-4"
            >
              {i18n.t('Manuals:addOnsCta')}
            </a>
          </span>
        </div>
        <div className="tw:grid tw:grid-cols-[repeat(auto-fill,minmax(260px,1fr))] tw:gap-4">
          {ADD_ONS.map((resource) => (
            <ResourceCard key={resource.key} resource={resource} isAddOn />
          ))}
        </div>
      </section>
    </div>
  );
};
